import { FC, Fragment, ReactNode, useEffect, useRef, useState } from "react"
import { IoMdClose } from "react-icons/io";

type DialogProps = {
	id: string;
	show: boolean;
	onClose: () => void;
	header: string;
	body: ReactNode;
	footer?: ReactNode;
	size?: 'sm' | 'md' | 'lg';
	modeless?: boolean;
	placement?: 'center' | 'bottom-right';
};

const SIZE_CLASS = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' };

const PLACEMENT_CLASS = {
	center: 'items-center justify-center',
	'bottom-right': 'items-end justify-end p-8 pb-20',
};

const Dialog: FC<DialogProps> = ({
	id, show, onClose, header, body, footer, size = 'md', modeless = false, placement = 'center',
}) => {

	const [isDragging, setIsDragging] = useState(false);
	const DialogRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const startPos = useRef({ x: 0, y: 0 });

	useEffect(() => {
		if (!isDragging) return undefined;
		const move = (e: MouseEvent) => setPosition({
			x: e.clientX - startPos.current.x,
			y: e.clientY - startPos.current.y,
		});
		const stop = () => setIsDragging(false);
		window.addEventListener('mousemove', move);
		window.addEventListener('mouseup', stop);
		return () => {
			window.removeEventListener('mousemove', move);
			window.removeEventListener('mouseup', stop);
		};
	}, [isDragging]);

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		if (target.closest('button') || target.closest('input')) return;
		if (target.closest('[data-draggable="true"]')) {
			e.preventDefault();
			startPos.current = { x: e.clientX - position.x, y: e.clientY - position.y };
			setIsDragging(true);
		}
	};

	const containerClass = modeless
		? `flex fixed inset-0 z-50 pointer-events-none ${PLACEMENT_CLASS[placement]}`
		: `flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none ${PLACEMENT_CLASS[placement]}`;

	return (show ?
		<Fragment>
			{!modeless && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />}
			<div
				id={id}
				className={containerClass}
				ref={DialogRef}
				style={{
					transform: `translate(${position.x}px, ${position.y}px)`,
				}}
				onMouseDown={handleMouseDown}
			>
				<div className={`relative w-auto my-6 ${placement === 'center' ? 'mx-auto' : ''} ${SIZE_CLASS[size]} ${modeless ? 'pointer-events-auto w-full' : ''}`}>

					<div role="dialog" aria-modal={!modeless} aria-label={header} className="border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg shadow-slate-400 hover:shadow-slate-600 dark:shadow-black relative flex flex-col w-full bg-white dark:bg-slate-800 outline-none focus:outline-none">

						<div
							data-draggable="true"
							style={{ cursor: isDragging ? "grabbing" : "grab" }}
							className="flex items-start justify-between p-2 border-b border-solid border-slate-200 dark:border-slate-600 rounded-t bg-slate-50 dark:bg-slate-700 select-none">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
								{header}
							</h3>

							<button
								id={`btn-${id}-close`}
								type="button"
								className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
								data-dialog-hide={id}
								onClick={onClose}
							>
								<IoMdClose />
								<span className="sr-only">Fechar a janela</span>
							</button>
						</div>

						<div>
							{body}
						</div>

						{footer && <div className="flex items-center justify-end py-2 px-6 border-t border-solid border-slate-200 dark:border-slate-600 rounded-b">
							{footer}
						</div>}
					</div>
				</div>
			</div>

		</Fragment> : null
	);
}

export default Dialog;
